import { ChasePaymentUkRequest } from '@/components/payment/CmgChaseUkForm/cmg-chase-payment-uk-request';
import { SubmitOrderRequestPaymentMethod, SubmitOrderRequestPaymentMethodType, TokenizedPaymentCard } from '@/models';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';

@Component({
  name: 'CmgCheckoutAuthorizePaymentCardModal',
})
export default class CmgCheckoutAuthorizePaymentCardModal extends Vue {

  public isPaymentProviderInitializing = true;
  public errorMessages: string[] = [];

  private checkoutId: string = '';

  get visible(): boolean {
    return this.$appStore.state.ui.modals.checkoutAuthorizePaymentCard.visible;
  }

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get args() {
    return this.$appStore.state.ui.modals.checkoutAuthorizePaymentCard.args;
  }

  get order() {
    return this.args?.order;
  }

  get orderAmount() {
    return this.order?.orderTotalAmount || 0;
  }

  get paymentCard(): SubmitOrderRequestPaymentMethod | undefined {
    return this.args?.paymentCard;
  }

  get providerId() {
    return this.args?.paymentCard.paymentProviderId;
  }

  get showErrors() {
    return this.errorMessages.length != 0;
  }

  get height() {
    return this.paymentCard?.paymentType === SubmitOrderRequestPaymentMethodType.ChipotleWalletCreditCard ? 250 : 420;
  }

  get isOneTimePayment() {
    return this.paymentCard?.paymentType === SubmitOrderRequestPaymentMethodType.CreditCard;
  }

  get header() {
    return this.isOneTimePayment ? 'Authorize Payment Card' : 'APPROVE THIS PAYMENT?';
  }

  get authorizationType() {
    return !this.isOneTimePayment ? 'walletauthorization' : 'onetimepayment';
  }

  @Watch('visible', { immediate: true })
  async visibleChanged(newVisible: boolean) {
    if (newVisible) {
      this.errorMessages = [];
      window.removeEventListener('message', this.listenerFunction);
      window.addEventListener("message", this.listenerFunction);

      try {
        await this.loadIframe();
        await this.setFrameHeight();
      } catch (e: any) {
        this.errorMessages.push('Oops an error was encontered while fetching the checkout id from the service. Please try again!');
      }
    }
  }

  private listenerFunction(ev: Event) {
    const event = ev as MessageEvent;
    if (event && event.origin === new URL(this.$services.payment.getChaseUKCallbackUrl()).origin  &&
        event.data && JSON.parse(event.data).id === this.checkoutId) {
        this.completePayment();
    }    
  }

  private async setFrameHeight() {
    document.getElementsByName('paymentIFrame')[0]!.style.height = (this.authorizationType === 'onetimepayment') ? '500px' : '250px';
  }

  private async loadIframe() {
    this.checkoutId = await this.getCheckoutId();
    const iframe = this.$refs.frame as HTMLIFrameElement;
    if (iframe) {
      iframe.src = `${this.$services.config.getChaseUkBaseUrl()}?checkoutId=${this.checkoutId}&authorizationType=${this.authorizationType}&amount=${this.orderAmount}`;
    }
  }

  private async getCheckoutId(): Promise<string> {
    try {
      const request: ChasePaymentUkRequest = new ChasePaymentUkRequest();

      request.preauth = true;
      request.amount = this.orderAmount;
      request.restaurantId = this.order!.restaurantId;
      request.tokenId = this.paymentCard!.chipotleWalletId;

      const response = await this.$services.transaction.initializeRequest(this.providerId!, request);
      if ((!response.uId || !response.sessionId) && !response.checkoutId) {
        throw Error('No request data returned by the service');
      } else {
        return response.checkoutId || '';
      }
    } catch (error: any) {
      this.$services.logger.error('An error was encountered while retrieving a checkout id from server.', error);
      throw new Error('The request session could not be fulfilled');
    } finally {
      this.isPaymentProviderInitializing = false;
    }
  }

  private async completePayment(): Promise<void> {
    this.isPaymentProviderInitializing = true;
    this.errorMessages = [];

    try {
      const tokenizedCardResponse = !this.isOneTimePayment
        ? await this.$services.transaction.getTransactionToken(this.providerId!, this.checkoutId, this.paymentCard!.chipotleWalletId?.toString(), this.order!.restaurantId, this.order!.orderId)
        : await this.$services.transaction.getTransactionToken(this.providerId!, this.checkoutId, undefined, this.order!.restaurantId, this.order!.orderId);
      const tokenizedCard = {
        cardHolderName: tokenizedCardResponse.cardHolderName,
        expiration: tokenizedCardResponse.expiration,
        paymentMethod: tokenizedCardResponse.paymentMethod,
        tokenizedAccountNumber: tokenizedCardResponse.paymentToken,
        paymentProviderId: this.providerId,
        lastFourAccountNumbers: tokenizedCardResponse.last4,
        transactionId: tokenizedCardResponse.authTransactionId?.toString()
      } as TokenizedPaymentCard;

      if (this.isOneTimePayment) {
        this.setOneTimePaymentMethod(tokenizedCard);
      } else {
        this.addTransactionIdToPaymentMethodAndSubmit(tokenizedCard.transactionId!);
      }

    } catch (err: any) {
      await this.loadIframe();
      this.errorMessages.push('Please verify information and try again!');
      this.isPaymentProviderInitializing = false;
    }

    this.isPaymentProviderInitializing = false;
    this.onClose();
  }

  private setOneTimePaymentMethod(tokenizedCard: TokenizedPaymentCard) {
    this.$services.checkout.setPaymentMethods([
      {
        cardHolderName: tokenizedCard.cardHolderName!,
        creditCardSingleUseToken: tokenizedCard.tokenizedAccountNumber!,
        creditCardType: tokenizedCard.paymentMethod!.replace(/-/g, ' '),
        creditCardExpiration: tokenizedCard.expiration,
        paymentType: SubmitOrderRequestPaymentMethodType.CreditCard,
        paymentProviderId: this.providerId,
        lastFourAccountNumbers: tokenizedCard.lastFourAccountNumbers,
        transactionId: tokenizedCard.transactionId,
        preauthorizedAmount: this.orderAmount
      },
    ]);
    if (tokenizedCard.expiration) {
      tokenizedCard.expirationMonth = tokenizedCard.expiration.substr(0, 2);
      tokenizedCard.expirationYear = tokenizedCard.expiration.substr(2, 2);
    }
    tokenizedCard.paymentMethod = tokenizedCard.paymentMethod.toLowerCase();
    tokenizedCard.isOneTimePaymentCard = true;
    this.$services.checkout.setOneTimePayCard(tokenizedCard);
  }

  private addTransactionIdToPaymentMethodAndSubmit(transactionId: string) {
    const card: SubmitOrderRequestPaymentMethod[] = [];
    this.paymentCard!.transactionId = transactionId;
    card.push(this.paymentCard!);
    this.$services.checkout.setPaymentMethods(card);
    this.onApprove();
  }

  onApprove() {
    if (this.args && this.args.onApprove) {
      this.args.onApprove();
    }

    this.close();
  }

  onClose() {
    if (this.args && this.args.onClose) {
      this.args.onClose();
    }

    this.close();
  }

  private close() {
    this.$services.ui.setModalVisibility('checkoutAuthorizePaymentCard', false);
    this.$services.ui.focusBack();
  }
}